import { ChevronDown, Globe, Heart, Moon, Sun, ThumbsUp, Users } from "lucide-react";
import { useState } from "react";
import { hasSocialVote, rememberSocialVote, recordSocialEvent } from "@/lib/siteEngagement";
import useCommunityStats from "@/hooks/useCommunityStats";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useLang } from "@/lib/i18n";
import { LANGS } from "@/lib/locales";
import { useTheme } from "@/lib/theme";

const optionClass = (active) =>
`relative flex-1 inline-flex items-center justify-center gap-1.5 overflow-hidden rounded-lg px-3 py-2 text-[12px] font-bold border transition-[transform,background,border-color,box-shadow] duration-200 active:scale-[0.985] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#ef4438]/70 ${
active ?
"border-[#171717] bg-gradient-to-b from-[#3a3a3a] to-[#171717] text-white shadow-[0_3px_8px_rgba(0,0,0,0.16),inset_0_1px_0_rgba(255,255,255,0.22)]" :
"border-[#deded9] bg-gradient-to-b from-white to-[#f0efeb] text-[#171717] shadow-[0_2px_5px_rgba(25,25,25,0.06),inset_0_1px_0_rgba(255,255,255,0.95)] hover:border-[#bdbbb4] hover:shadow-[0_3px_7px_rgba(25,25,25,0.09),inset_0_1px_0_rgba(255,255,255,0.95)]"}`;


/** Site-wide tools: light/dark appearance and the visitor's language. */
export default function SiteTools({ className = "" }) {
  const { theme, setTheme } = useTheme();
  const { lang, setLang, t } = useLang();
  const current = LANGS.find((l) => l.code === lang) || LANGS[0];
  const stats = useCommunityStats();
  const [hasVoted, setHasVoted] = useState(() => hasSocialVote());
  const [ownVote, setOwnVote] = useState(0);
  const visitorsLabel = stats ? stats.visitors.toLocaleString() : "—";
  const helpfulLabel = stats ? (stats.helpful + ownVote).toLocaleString() : "—";
  const voteHelpful = async () => {
    if (hasVoted) return;
    // Record first; only persist the local vote after the server accepts it.
    try {
      await recordSocialEvent("helpful");
      rememberSocialVote("helpful");
      setHasVoted(true);
      setOwnVote(1);
    } catch {}
  };
  const es = lang === "es";

  return (
    <Popover>
      <PopoverTrigger asChild>
        






        
        <button
          type="button"
          aria-label={t("tools.title")}
          className={`relative inline-flex h-9 items-center gap-1.5 overflow-hidden rounded-full border border-black/[0.12] dark:border-white/[0.14] bg-gradient-to-b from-white to-[#f1f0ec] dark:from-white/[0.11] dark:to-white/[0.035] px-2.5 text-[#171717] dark:text-[#f5f2eb] shadow-[0_2px_6px_rgba(20,18,14,0.08),inset_0_1px_0_rgba(255,255,255,0.95)] dark:shadow-[0_2px_6px_rgba(0,0,0,0.14),inset_0_1px_0_rgba(255,255,255,0.18)] hover:border-black/25 dark:hover:border-white/25 hover:-translate-y-px active:scale-[0.985] transition-[transform,border-color,box-shadow] duration-200 ${className}`}
        >
          <Globe className="w-4 h-4" />
          <span className="font-mono text-[10px] tracking-[0.12em] font-bold">{current.short}</span>
          {theme === "dark" ? <Moon className="w-3.5 h-3.5 opacity-70" /> : <Sun className="w-3.5 h-3.5 opacity-70" />}
          <ChevronDown className="w-3 h-3 opacity-55" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[240px] p-0 rounded-[16px] overflow-hidden">
        <div className="px-3.5 pt-3.5 pb-3">
          <div className="font-mono text-[9.5px] tracking-[0.18em] uppercase font-bold text-[#6d6d6a] mb-2">
            {t("tools.appearance")}
          </div>
          <div className="flex gap-2">
            <button type="button" onClick={() => setTheme("light")} className={optionClass(theme === "light")}>
              <Sun className="w-4 h-4" /> {t("tools.light")}
            </button>
            <button type="button" onClick={() => setTheme("dark")} className={optionClass(theme === "dark")}>
              <Moon className="w-4 h-4" /> {t("tools.dark")}
            </button>
          </div>
        </div>

        <div className="px-3.5 pt-3 pb-3.5 border-t border-black/[0.08]">
          <div className="font-mono text-[9.5px] tracking-[0.18em] uppercase font-bold text-[#6d6d6a] mb-2">
            {t("tools.language")}
          </div>
          <div className="space-y-1.5">
            {LANGS.map((l) =>
            <button
              key={l.code}
              type="button"
              onClick={() => setLang(l.code)}
              className={`w-full inline-flex items-center gap-2 rounded-lg px-3 py-2 text-[12.5px] font-bold border transition-[background,border-color,box-shadow] duration-200 ${
              l.code === lang ?
              "bg-gradient-to-b from-[#f1f0eb] to-[#e6e4dd] text-[#171717] border-[#171717] shadow-[inset_0_1px_0_rgba(255,255,255,0.95),0_2px_5px_rgba(0,0,0,0.07)]" :
              "bg-gradient-to-b from-white to-[#f4f3ef] text-[#171717] border-[#deded9] shadow-[inset_0_1px_0_rgba(255,255,255,0.95)] hover:border-[#bdbbb4] hover:shadow-[0_2px_5px_rgba(0,0,0,0.07),inset_0_1px_0_rgba(255,255,255,0.95)]"}`
              }>
              
                <Globe className="w-3.5 h-3.5" />
                {l.label}
                {l.code === lang && <span className="ml-auto font-mono text-[9.5px] tracking-widest">{l.short}</span>}
              </button>
            )}
          </div>
        </div>

        <div className="border-t border-black/[0.08] px-3.5 py-3.5">
          <div className="font-mono text-[9.5px] tracking-[0.18em] uppercase font-bold text-[#6d6d6a] mb-2">
            {es ? "Nuestra comunidad" : "Our community"}
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div className="rounded-[11px] border border-black/[0.07] bg-[#f7f6f2] px-2.5 py-2.5">
              <div className="flex items-center gap-1.5 text-[#77746d]"><Users className="h-3.5 w-3.5" /><span className="text-[10px] font-semibold">{es ? "Visitantes" : "Visitors"}</span></div>
              <div className="mt-1 text-[20px] font-black tracking-[-0.04em] text-[#171717]">{visitorsLabel}</div>
            </div>
            <div className="rounded-[11px] border border-black/[0.07] bg-[#f7f6f2] px-2.5 py-2.5">
              <div className="flex items-center gap-1.5 text-[#77746d]"><ThumbsUp className="h-3.5 w-3.5" /><span className="text-[10px] font-semibold">{es ? "Útil" : "Found it helpful"}</span></div>
              <div className="mt-1 text-[20px] font-black tracking-[-0.04em] text-[#171717]">{helpfulLabel}</div>
              <button type="button" onClick={voteHelpful} disabled={hasVoted} className="mt-2 inline-flex w-full items-center justify-center gap-1 rounded-full border border-black/10 bg-white px-2 py-1.5 text-[10px] font-bold text-[#34332f] disabled:cursor-default disabled:opacity-60 hover:bg-[#f0efeb]">
                <ThumbsUp className="h-3 w-3" /> {hasVoted ? (es ? "¡Gracias!" : "Thanks!") : (es ? "Sí, es útil" : "Yes, helpful")}
              </button>
            </div>
          </div>
          <div className="mt-2 flex items-start gap-2 rounded-[10px] bg-[#b84a3a]/[0.06] px-2.5 py-2.5">
            <Heart className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[#b84a3a]" />
            <p className="text-[10.5px] leading-[1.45] text-[#68665f]">{es ? "Esta guía es gratuita y crece gracias a su comunidad." : "This guide is free and grows with the support of its community."}</p>
          </div>
        </div>
      </PopoverContent>
    </Popover>);

}